import { useEffect } from 'react';
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  CreditCard,
  Folder,
  Image as ImageIcon,
  LayoutDashboard,
  LogOut,
  User2,
} from 'lucide-react';
import { Container } from '@/components/layout/container';
import { Logo } from '@/components/common/logo';
import { Button } from '@/components/ui/button';
import { SkipLink } from '@/components/common/skip-link';
import { SeoHead } from '@/components/common/seo-head';
import { useAuth, useAuthBootstrap } from '@/stores/auth';
import { signOutAndRedirect } from '@/lib/auth-client';
import { resolvePublicAppUrl } from '@/lib/seo';
import { cn } from '@/lib/cn';

// Module scope — no component dependencies, stable across renders.
const onSignOut = () => { void signOutAndRedirect(() => { window.location.href = '/'; }); };

const navItems = [
  { to: '/account', label: 'Account', icon: User2, end: true },
  { to: '/account/generations', label: 'Your creations', icon: ImageIcon },
  { to: '/collections', label: 'Collections', icon: Folder },
] as const;

const footerNavItems = [
  { to: '/pricing', label: 'Pricing', icon: CreditCard },
] as const;

const NavItem = ({
  to,
  label,
  icon: Icon,
  end,
  compact,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  compact?: boolean;
}) => {
  const location = useLocation();
  const isActive =
    end === true
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <NavLink
      to={to}
      end={end}
      className={cn(
        'flex items-center gap-2.5 rounded-md text-sm transition-colors whitespace-nowrap',
        compact ? 'px-3 py-1.5' : 'px-3 py-2',
        isActive
          ? 'bg-accent-soft text-foreground ring-1 ring-accent/20'
          : 'text-text-secondary hover:text-foreground hover:bg-surface-hover',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {label}
    </NavLink>
  );
};

export const UserLayout = () => {
  useAuthBootstrap();
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/signin', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading || !isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-text-muted">Loading your library...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead
        publicAppUrl={resolvePublicAppUrl()}
        title="Your library"
        description="Your saved prompts, collections, and generation history on Visualry."
        noindex
      />
      <SkipLink />
      <header className="sticky top-0 z-30 border-b border-border-subtle bg-background/88 backdrop-blur-xl">
        <Container size="xl" className="flex h-14 items-center gap-3 sm:gap-4">
          <Logo size="sm" />
          <span className="text-[10px] uppercase tracking-widest text-accent border border-accent/40 rounded-full px-2 py-0.5 bg-accent-soft">
            Your library
          </span>
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            <Link to="/explore">
              <ArrowLeft className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Back to Explore</span>
            </Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </Container>
      </header>

      <nav
        aria-label="Library"
        className="md:hidden border-b border-border-subtle bg-surface-soft/55 overflow-x-auto"
      >
        <div className="flex gap-1 px-3 py-2 min-w-max">
          {navItems.map((item) => (
            <NavItem {...item} key={item.to} compact />
          ))}
          {footerNavItems.map((item) => (
            <NavItem {...item} key={item.to} compact />
          ))}
          {user.role === 'admin' && (
            <NavItem to="/admin" label="Admin" icon={LayoutDashboard} compact />
          )}
        </div>
      </nav>

      <div className="flex-1 flex">
        <aside className="hidden md:block w-56 shrink-0 border-r border-border-subtle bg-surface-soft/40 backdrop-blur">
          <nav className="p-3 space-y-0.5">
            {navItems.map((item) => (
              <NavItem {...item} key={item.to} />
            ))}
            <div className="my-2 border-t border-border-subtle" />
            {footerNavItems.map((item) => (
              <NavItem {...item} key={item.to} />
            ))}
            {user.role === 'admin' && (
              <NavItem to="/admin" label="Admin" icon={LayoutDashboard} />
            )}
          </nav>
        </aside>
        <main id="main" tabIndex={-1} className="flex-1 min-w-0">
          <Container size="xl" className="py-6 sm:py-8">
            <Outlet />
          </Container>
        </main>
      </div>
      <ScrollRestoration />
    </div>
  );
};
